import { getCaseStudyBySlug } from '@/content/work'
import { getServiceBySlug, servicePages } from '@/content/services'

/** Production site origin - update if your canonical domain differs. */
export const SITE_URL =
  (import.meta.env.NEXT_PUBLIC_SITE_URL as string | undefined)?.replace(/\/$/, '') ||
  'https://www.elementarycreative.com'

export const SITE_NAME = 'Elementary Creative'

export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-default.png`

export const DEFAULT_DESCRIPTION =
  'Elementary Creative is a Vancouver digital product studio building high-performing websites and custom software including POS, booking, CRM, and internal tools.'

export type SeoConfig = {
  title: string
  description: string
  /** Path only, e.g. /contact */
  path: string
  /** Set false for admin or noindex pages */
  index?: boolean
  ogType?: 'website' | 'article'
  ogImage?: string
}

/** Public marketing routes - used for meta tags. Keep paths in sync with src/content/catalog.json. */
export const PUBLIC_PAGES: SeoConfig[] = [
  {
    path: '/',
    title: 'Elementary Creative | Web Design & Custom Software - Vancouver',
    description: DEFAULT_DESCRIPTION,
  },
  {
    path: '/services',
    title: 'Web Design, Development & Custom Software | Elementary Creative',
    description:
      'Vancouver studio for marketing sites, POS, booking systems, CRM, and internal tools. Strategy, design, and engineering for founder-led teams.',
  },
  ...servicePages.map((page) => ({
    path: page.path,
    title: page.seoTitle,
    description: page.seoDescription,
  })),
  {
    path: '/portfolio',
    title: 'Web Design Portfolio & Case Studies | Elementary Creative',
    description:
      'See live client websites and product experiments from Elementary Creative - Vancouver web design, custom software, and digital product launches.',
  },
  {
    path: '/contact',
    title: 'Contact Elementary Creative | Vancouver Web Design Studio',
    description:
      'Get in touch with Elementary Creative in Vancouver. Tell us about your website, app, or internal tool project - we typically reply within one business day.',
  },
  {
    path: '/connect',
    title: 'Connect With Elementary Creative | Links & Scheduling',
    description:
      'Find Elementary Creative online - website, portfolio, Instagram, and book a free 30-minute consultation call with our Vancouver team.',
  },
  {
    path: '/blog',
    title: 'Blog | Elementary Creative - Web Design & Product Insights',
    description:
      'Articles on web design, custom software, and digital products from Elementary Creative, a Vancouver studio serving local and remote clients.',
    index: false,
  },
  {
    path: '/privacy-policy',
    title: 'Privacy Policy | Elementary Creative',
    description:
      'Privacy policy for Elementary Creative - how we collect, use, and protect information when you visit our website or contact our Vancouver studio.',
  },
]

export function getSeoForPath(pathname: string): SeoConfig {
  const path = pathname.split('?')[0].replace(/\/$/, '') || '/'
  const exact = PUBLIC_PAGES.find((p) => p.path === path)
  if (exact) return exact

  if (path.startsWith('/admin')) {
    return {
      path,
      title: `Admin | ${SITE_NAME}`,
      description: DEFAULT_DESCRIPTION,
      index: false,
    }
  }

  if (path.startsWith('/blog/')) {
    return {
      path,
      title: `Blog | ${SITE_NAME}`,
      description: DEFAULT_DESCRIPTION,
      index: false,
    }
  }

  if (path.startsWith('/work/')) {
    const study = getCaseStudyBySlug(path.slice('/work/'.length))
    if (study) {
      return {
        path: study.path,
        title: study.seoTitle,
        description: study.seoDescription,
      }
    }
  }

  if (path.startsWith('/services/')) {
    const service = getServiceBySlug(path.slice('/services/'.length))
    if (service) {
      return {
        path: service.path,
        title: service.seoTitle,
        description: service.seoDescription,
      }
    }
  }

  return {
    path: '/',
    title: `${SITE_NAME} | Web Design & Custom Software - Vancouver`,
    description: DEFAULT_DESCRIPTION,
    index: false,
  }
}
